import { useEffect, useRef, useState } from "react";
import AdminLayout from "../../components/admin/AdminLayout";
import { Search, Car, BedDouble, UserRound, ChevronLeft, ChevronRight, ChevronDown, Copy, Check } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import adminService from "../../api/services/adminService";
import { useAppConfigContext } from "../../context/AppConfigContext";
import { formatCurrency as formatCurrencyValue } from "../../utils/currency";

const toNumber = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return value;
  const cleaned = String(value).replace(/[^0-9.-]/g, "");
  if (!cleaned) return null;
  const parsed = Number(cleaned);
  return Number.isNaN(parsed) ? null : parsed;
};

const getFirstValue = (source, keys) => {
  if (!source) return null;
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(source, key) && source[key] !== undefined) {
      return source[key];
    }
  }
  return null;
};

// Falls back to NGN formatting when no currency label is supplied (e.g. from
// module-level helpers that run before the settings-driven currency is
// known); components should pass the live primaryCurrency from
// useAppConfigContext wherever possible.
const formatCurrency = (value, currencyLabel) => {
  const num = toNumber(value);
  if (num === null) return "—";
  return formatCurrencyValue(num, currencyLabel);
};

const formatCount = (value) => {
  const num = toNumber(value);
  if (num === null) return "—";
  return num.toLocaleString();
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString();
};

const formatServiceLabel = (service) => {
  const value = String(service || "").trim();
  if (!value) return "—";
  return value.charAt(0).toUpperCase() + value.slice(1);
};

// Revenue by service is calculated by the backend (financial-summary), using
// the same rule as Total Revenue: unpaid bookings excluded, refunds netted out.
// The page only styles the rows it returns.
const SERVICE_COLORS = {
  assistant: "#6b2b5b",
  accommodation: "#6b2b5b",
  ride: "#4b5563",
};

const DEFAULT_REVENUE_BY_SERVICE = [
  { service: "assistant", label: "Pulo Assistants", revenue: 0, percentage: 0 },
  { service: "accommodation", label: "Accommodation", revenue: 0, percentage: 0 },
  { service: "ride", label: "Rides", revenue: 0, percentage: 0 },
];

const toRevenueByServiceRows = (items, currencyLabel) =>
  (Array.isArray(items) && items.length ? items : DEFAULT_REVENUE_BY_SERVICE).map((item) => {
    const percent = toNumber(item.percentage) ?? 0;
    return {
      label: item.label || formatServiceLabel(item.service),
      amount: formatCurrency(toNumber(item.revenue) ?? 0, currencyLabel),
      percent: `${percent.toFixed(2)}%`,
      width: `${Math.min(Math.max(percent, 0), 100)}%`,
      color: SERVICE_COLORS[item.service] || "#9ca3af",
    };
  });

const FAILED_STATUSES = ["failed", "failed_payment", "canceled", "cancelled", "reversed"];

const getStatusClass = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (["paid", "success", "successful", "completed"].includes(normalized)) {
    return "bg-green-100 text-[#008000]";
  }
  if (["pending", "processing", "in_progress"].includes(normalized)) {
    return "bg-yellow-100 text-[#92400E]";
  }
  if (FAILED_STATUSES.includes(normalized)) {
    return "bg-red-100 text-[#B91C1C]";
  }
  return "bg-gray-100 text-gray-700";
};

const formatStatus = (status) => {
  if (!status || status === "—") return "—";
  return String(status)
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const truncateId = (id, startLen = 10, endLen = 6) => {
  if (!id) return "—";
  const str = String(id);
  if (str.length <= startLen + endLen + 3) return str;
  return `${str.slice(0, startLen)}...${str.slice(-endLen)}`;
};

const CopyableId = ({ id }) => {
  const [copied, setCopied] = useState(false);
  const displayId = truncateId(id);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(String(id));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // silently fail
    }
  };

  return (
    <div className="flex items-center gap-2" title={String(id)}>
      <span className="font-mono text-sm text-gray-700">{displayId}</span>
      <button
        onClick={handleCopy}
        className="p-1 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
        aria-label="Copy transaction ID"
        title="Copy transaction ID"
        type="button"
      >
        {copied ? (
          <Check className="w-3.5 h-3.5 text-green-600" />
        ) : (
          <Copy className="w-3.5 h-3.5" />
        )}
      </button>
    </div>
  );
};

const normalizeTrendSeries = (payload) => {
  if (!payload) return [];

  if (Array.isArray(payload?.labels)) {
    const labels = payload.labels;
    const revenueSeries =
      payload.revenue ||
      payload.revenues ||
      payload.totalRevenue ||
      payload.total_revenue ||
      payload.series?.revenue ||
      payload.series?.totalRevenue;

    return labels.map((label, index) => ({
      label: String(label),
      revenue: toNumber(revenueSeries?.[index]) ?? 0,
    }));
  }

  const candidates =
    payload?.data ||
    payload?.items ||
    payload?.results ||
    payload?.series ||
    payload?.points ||
    payload?.chart ||
    payload;

  if (!Array.isArray(candidates)) return [];

  return candidates.map((item, index) => {
    if (typeof item === "number") {
      return { label: String(index + 1), revenue: item };
    }

    const label =
      item?.label ||
      item?.day ||
      item?.date ||
      item?.month ||
      item?.period ||
      item?.name ||
      item?.x ||
      String(index + 1);
    const value =
      item?.revenue ||
      item?.totalRevenue ||
      item?.total_revenue ||
      item?.amount ||
      item?.value ||
      item?.y;

    return { label: String(label), revenue: toNumber(value) ?? 0 };
  });
};

const normalizeTrendsPayload = (payload) => {
  const source = payload?.data ?? payload;
  const dailyPayload =
    source?.daily || source?.last7Days || source?.last_7_days || source?.week || source?.days;
  const monthlyPayload =
    source?.monthly ||
    source?.last12Months ||
    source?.last_12_months ||
    source?.year ||
    source?.months;

  return {
    daily: normalizeTrendSeries(dailyPayload),
    monthly: normalizeTrendSeries(monthlyPayload),
  };
};

const normalizeTransactionsPayload = (payload) => {
  const source = payload?.data ?? payload ?? {};
  const items =
    source?.items ||
    source?.results ||
    source?.transactions ||
    source?.data ||
    source?.rows ||
    [];
  const meta = source?.meta || source?.pagination || source;

  const normalizedItems = Array.isArray(items)
    ? items.map((item, index) => {
        const user = item?.user || item?.customer || item?.client || item?.buyer || {};
        const name =
          user?.name ||
          user?.fullName ||
          [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
          "—";
        const email =
          user?.email ||
          user?.emailAddress ||
          user?.email_address ||
          "—";
        const service =
          item?.service ||
          item?.serviceType ||
          item?.service_type ||
          item?.bookingType ||
          item?.category ||
          item?.type ||
          item?.booking?.type ||
          "";
        const transactionId =
          item?.transactionId ||
          item?.transaction_id ||
          item?.reference ||
          item?.paymentReference ||
          item?.payment_reference ||
          item?.id ||
          `#${index + 1}`;
        // `transactionAmount` is the canonical field on
        // GET /admin/dashboard/transactions (it already resolves
        // amountPaid -> amountToCharge -> amountExpected server-side); the
        // remaining keys are legacy/aliased shapes kept for safety.
        // getFirstValue is used instead of a `||` chain so a legitimate 0 on
        // a failed payment is not mistaken for a missing field.
        const amount = getFirstValue(item, [
          "transactionAmount",
          "transaction_amount",
          "amount",
          "totalAmount",
          "total_amount",
          "amountPaid",
          "paidAmount",
          "paid_amount",
          "price",
        ]);
        const status =
          item?.status ||
          item?.paymentStatus ||
          item?.payment_status ||
          item?.state ||
          "—";
        const createdAt =
          item?.createdAt ||
          item?.created_at ||
          item?.paidAt ||
          item?.paid_at ||
          item?.date ||
          "";

        return {
          id: transactionId,
          userName: name,
          userEmail: email,
          service,
          amount,
          status,
          createdAt,
        };
      })
    : [];

  return {
    items: normalizedItems,
    total:
      meta?.total ||
      meta?.totalItems ||
      meta?.total_items ||
      meta?.count ||
      normalizedItems.length,
    page: meta?.page || meta?.currentPage || meta?.current_page || 1,
    limit: meta?.limit || meta?.perPage || meta?.per_page || normalizedItems.length,
  };
};

function AdminFinances() {
  const { config } = useAppConfigContext();
  const currencyLabel = config.primaryCurrency;
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("createdAt:desc");
  const [sortOpen, setSortOpen] = useState(false);
  const [sortQuery, setSortQuery] = useState("");
  const sortRef = useRef(null);
  const [timeframe, setTimeframe] = useState("Last 7 days");
  const [timeframeOpen, setTimeframeOpen] = useState(false);
  const [timeframeQuery, setTimeframeQuery] = useState("");
  const timeframeRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const [totalItems, setTotalItems] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [transactionsError, setTransactionsError] = useState("");
  const [financialSummary, setFinancialSummary] = useState(null);
  const [financialSummaryLoading, setFinancialSummaryLoading] = useState(false);
  const [financialSummaryError, setFinancialSummaryError] = useState("");
  const [trends, setTrends] = useState({ daily: [], monthly: [] });
  const [trendsLoading, setTrendsLoading] = useState(false);
  const [trendsError, setTrendsError] = useState("");
  const [reportDownloading, setReportDownloading] = useState(false);
  const [reportError, setReportError] = useState("");

  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const revenueByService = toRevenueByServiceRows(
    financialSummary?.revenueByService,
    currencyLabel
  );

  useEffect(() => {
    const handleOutside = (event) => {
      if (timeframeRef.current && !timeframeRef.current.contains(event.target)) {
        setTimeframeOpen(false);
      }
      if (sortRef.current && !sortRef.current.contains(event.target)) {
        setSortOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  const timeframeOptions = [
    "Last 7 days",
    "Last 1 month",
    "Last 3 months",
    "Last 1 year",
  ];
  const filteredTimeframeOptions = timeframeQuery.trim()
    ? timeframeOptions.filter((option) =>
        option.toLowerCase().includes(timeframeQuery.trim().toLowerCase())
      )
    : timeframeOptions;

  const sortOptions = [
    { label: "Most Recent", value: "createdAt:desc" },
    { label: "Oldest First", value: "createdAt:asc" },
    { label: "Amount (High-Low)", value: "transactionAmount:desc" },
    { label: "Amount (Low-High)", value: "transactionAmount:asc" },
    { label: "Net Profit (High-Low)", value: "netProfit:desc" },
    { label: "Net Profit (Low-High)", value: "netProfit:asc" },
    { label: "Status (A-Z)", value: "status:asc" },
    { label: "Status (Z-A)", value: "status:desc" },
  ];
  const currentSortLabel =
    sortOptions.find((option) => option.value === sortBy)?.label || "Most Recent";
  const filteredSortOptions = sortQuery.trim()
    ? sortOptions.filter((option) =>
        option.label.toLowerCase().includes(sortQuery.trim().toLowerCase())
      )
    : sortOptions;

  useEffect(() => {
    let isActive = true;

    const fetchFinancialSummary = async () => {
      setFinancialSummaryLoading(true);
      setFinancialSummaryError("");
      try {
        const data = await adminService.getFinancialSummary();
        if (!isActive) return;
        setFinancialSummary(data?.data ?? data);
      } catch (err) {
        if (!isActive) return;
        setFinancialSummaryError(err?.message || "Failed to load financial summary.");
        setFinancialSummary(null);
      } finally {
        if (isActive) setFinancialSummaryLoading(false);
      }
    };

    const fetchTrends = async () => {
      setTrendsLoading(true);
      setTrendsError("");
      try {
        const data = await adminService.getRevenueTrends();
        if (!isActive) return;
        setTrends(normalizeTrendsPayload(data));
      } catch (err) {
        if (!isActive) return;
        setTrendsError(err?.message || "Failed to load revenue trends.");
        setTrends({ daily: [], monthly: [] });
      } finally {
        if (isActive) setTrendsLoading(false);
      }
    };

    fetchFinancialSummary();
    fetchTrends();
    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    let isActive = true;

    const fetchTransactions = async () => {
      setTransactionsLoading(true);
      setTransactionsError("");
      try {
        const data = await adminService.getDashboardTransactions({
          search: searchQuery || undefined,
          sortBy,
          page: currentPage,
          limit: itemsPerPage,
        });
        if (!isActive) return;
        const normalized = normalizeTransactionsPayload(data);
        setTransactions(normalized.items);
        setTotalItems(normalized.total);
      } catch (err) {
        if (!isActive) return;
        setTransactionsError(err?.message || "Failed to load transactions.");
        setTransactions([]);
        setTotalItems(0);
      } finally {
        if (isActive) setTransactionsLoading(false);
      }
    };

    fetchTransactions();
    return () => {
      isActive = false;
    };
  }, [searchQuery, sortBy, currentPage, itemsPerPage]);

  const chartData =
    timeframe === "Last 7 days" ? trends.daily : trends.monthly;

  const financialSource = financialSummary?.totals ?? financialSummary ?? {};
  const totalRevenue = getFirstValue(financialSource, [
    "totalRevenue",
    "total_revenue",
    "revenue",
    "totalRevenueAmount",
    "total_revenue_amount",
  ]);
  const totalNetProfit = getFirstValue(financialSource, [
    "totalNetProfit",
    "total_net_profit",
    "netProfit",
    "net_profit",
    "profit",
  ]);

  const kpiCards = [
    { title: "Total Revenue", value: formatCurrency(totalRevenue, currencyLabel), change: "—" },
    { title: "Net Profit", value: formatCurrency(totalNetProfit, currencyLabel), change: "—" },
    { title: "Total Transactions", value: formatCount(totalItems), change: "—" },
  ];

  const handleDownloadReport = async () => {
    setReportDownloading(true);
    setReportError("");
    try {
      const fileBlob = await adminService.downloadDashboardReport({ format: "json" });
      const blob = fileBlob instanceof Blob ? fileBlob : new Blob([fileBlob]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "admin-dashboard-report.json");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setReportError(err?.message || "Failed to download report.");
    } finally {
      setReportDownloading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">Finance Overview</h1>
          <p className="text-sm text-gray-600">Tracking revenue, expense and profit across all users</p>
          {reportError ? <p className="text-xs text-red-600 mt-1">{reportError}</p> : null}
          {financialSummaryError ? <p className="text-xs text-red-600 mt-1">{financialSummaryError}</p> : null}
          {financialSummaryLoading ? <p className="text-xs text-gray-500 mt-1">Loading financial summary...</p> : null}
        </div>
        <button
          type="button"
          onClick={handleDownloadReport}
          disabled={reportDownloading}
          className="inline-flex items-center gap-2 rounded-lg bg-[#570033] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#61013B] disabled:opacity-60"
        >
          <svg
            className="h-4 w-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <path d="M7 10l5 5 5-5" />
            <path d="M12 15V3" />
          </svg>
          {reportDownloading ? "Downloading..." : "Download Report"}
        </button>
      </div>
      <div className="space-y-6">
        
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {kpiCards.map((card) => (
            <div key={card.title} className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
              <div className="flex items-start justify-between">
                <h3 className="text-md font-medium text-gray-500">{card.title}</h3>
                <span className="rounded-md bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">
                  {card.change}
                </span>
              </div>
              <p className="mt-2 text-2xl font-semibold text-gray-900">{card.value}</p>
            </div>
          ))}
        </div>

        {/* Revenue Trends Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 lg:col-span-2">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Revenue Trends</h3>
              {trendsError ? (
                <p className="text-xs text-red-600">{trendsError}</p>
              ) : trendsLoading ? (
                <p className="text-xs text-gray-500">Loading trends...</p>
              ) : null}
              <div className="relative" ref={timeframeRef}>
                <button
                  type="button"
                  onClick={() => setTimeframeOpen((prev) => !prev)}
                  className="inline-flex items-center gap-3 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm"
                >
                  <span>{timeframe}</span>
                  <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${timeframeOpen ? "rotate-180" : ""}`} />
                </button>
                {timeframeOpen && (
                  <div className="absolute right-0 top-full z-20 mt-2 w-48 rounded-xl border border-gray-200 bg-white p-2 shadow-lg">
                    <div className="px-2 pb-2">
                      <input
                        type="text"
                        value={timeframeQuery}
                        onChange={(e) => setTimeframeQuery(e.target.value)}
                        placeholder="Search range"
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-[#570033] focus:outline-none focus:ring-2 focus:ring-[#570033]/20"
                      />
                    </div>
                    <div className="max-h-48 overflow-auto">
                      {filteredTimeframeOptions.length ? (
                        filteredTimeframeOptions.map((option) => (
                          <button
                            key={option}
                            type="button"
                            onClick={() => {
                              setTimeframe(option);
                              setTimeframeOpen(false);
                              setTimeframeQuery("");
                            }}
                            className={`w-full rounded-lg px-3 py-2 text-left text-sm font-medium text-gray-700 hover:bg-gray-50 ${
                              option === timeframe ? "bg-gray-50 text-gray-900" : ""
                            }`}
                          >
                            {option}
                          </button>
                        ))
                      ) : (
                        <p className="px-3 py-2 text-sm text-gray-500">No results found</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="mt-4 rounded-lg border border-gray-200 px-3 pb-4 pt-2">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData} margin={{ top: 10, right: 16, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="4 4" stroke="#d1d5db" vertical={false} />
                  <XAxis dataKey="label" stroke="#6b7280" fontSize={12} tickLine={false} axisLine={false} />
                  <YAxis stroke="#6b7280" fontSize={12} tickLine={false} axisLine={false} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#ffffff",
                      border: "1px solid #e5e7eb",
                      borderRadius: "10px",
                      padding: "10px 12px",
                    }}
                    labelStyle={{ color: "#111827", fontWeight: 600, marginBottom: "4px" }}
                    formatter={(value) => [formatCurrency(value, currencyLabel), "Revenue"]}
                  />
                  <Bar dataKey="revenue" fill="#6b2b5b" radius={[10, 10, 0, 0]} barSize={40} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <h4 className="text-sm font-semibold text-gray-900 mb-4">Revenue by Service</h4>
            <div className="space-y-6">
              {revenueByService.map((item) => (
                <div key={item.label}>
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: item.color }} />
                    <span>{item.label}</span>
                  </div>
                  <div className="mt-2 h-2 rounded-full bg-gray-100 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: item.width, backgroundColor: item.color }} />
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    {item.amount} <span className="ml-1">({item.percent})</span>
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Transaction List Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {transactionsError ? (
            <div className="px-6 py-3 text-sm text-red-600">{transactionsError}</div>
          ) : null}
          <div className="p-6 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search transaction by ID or User...."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white py-3 pl-11 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#570033]/20"
                />
              </div>
            </div>

            <div className="relative" ref={sortRef}>
              <button
                type="button"
                onClick={() => setSortOpen((prev) => !prev)}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 shadow-sm"
              >
                <span>
                  Sort by: <span className="font-semibold">{currentSortLabel}</span>
                </span>
                <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${sortOpen ? "rotate-180" : ""}`} />
              </button>
              {sortOpen && (
                <div className="absolute right-0 top-full z-20 mt-2 w-56 rounded-xl border border-gray-200 bg-white p-2 shadow-lg">
                  <div className="px-2 pb-2">
                    <input
                      type="text"
                      value={sortQuery}
                      onChange={(e) => setSortQuery(e.target.value)}
                      placeholder="Search sort option"
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-[#570033] focus:outline-none focus:ring-2 focus:ring-[#570033]/20"
                    />
                  </div>
                  <div className="max-h-48 overflow-auto">
                    {filteredSortOptions.length ? (
                      filteredSortOptions.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => {
                            setSortBy(option.value);
                            setCurrentPage(1);
                            setSortOpen(false);
                            setSortQuery("");
                          }}
                          className={`w-full rounded-lg px-3 py-2 text-left text-sm font-medium text-gray-700 hover:bg-gray-50 ${
                            option.value === sortBy ? "bg-gray-50 text-gray-900" : ""
                          }`}
                        >
                          {option.label}
                        </button>
                      ))
                    ) : (
                      <p className="px-3 py-2 text-sm text-gray-500">No results found</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-white border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">
                    TRANSACTION ID
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">USER</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">SERVICE</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">DATE</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">AMOUNT</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">STATUS</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {transactionsLoading && transactions.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-6 text-sm text-gray-500">
                      Loading transactions...
                    </td>
                  </tr>
                ) : transactions.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-6 text-sm text-gray-500">
                      No transactions found.
                    </td>
                  </tr>
                ) : (
                  transactions.map((transaction) => {
                    const serviceLabel = String(transaction.service || "");
                    const ServiceIcon = serviceLabel.toLowerCase().includes("ride")
                      ? Car
                      : serviceLabel.toLowerCase().includes("accommodation")
                        ? BedDouble
                        : UserRound;
                    return (
                      <tr key={transaction.id} className="hover:bg-gray-50">
                        <td className="px-6 py-6">
                          <CopyableId id={transaction.id} />
                        </td>
                        <td className="px-6 py-6">
                          <div className="text-sm">
                            <p className="font-semibold text-lg text-gray-800">
                              {transaction.userName}
                            </p>
                            <p className="text-gray-600 font-medium">{transaction.userEmail}</p>
                          </div>
                        </td>
                        <td className="px-6 py-6">
                          <div className="flex items-center gap-2 text-md text-gray-900 font-medium">
                            <ServiceIcon className="w-5 h-5 text-[#6b2b5b]" />
                            <span>{formatServiceLabel(serviceLabel)}</span>
                          </div>
                        </td>
                        <td className="px-6 py-6">
                          <p className="text-md font-medium text-gray-800">
                            {formatDate(transaction.createdAt)}
                          </p>
                        </td>
                        <td className="px-6 py-6">
                          <p className="text-md font-semibold text-gray-800">
                            {formatCurrency(transaction.amount, currencyLabel)}
                          </p>
                        </td>
                        <td className="px-6 py-6">
                          <span
                            className={`inline-block px-3 py-1 rounded-lg text-sm font-medium whitespace-nowrap ${getStatusClass(
                              transaction.status
                            )}`}
                            title={String(transaction.status || "")}
                          >
                            {formatStatus(transaction.status)}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-700">
              Showing{" "}
              <span className="font-medium">
                {transactions.length ? (currentPage - 1) * itemsPerPage + 1 : 0}
              </span>{" "}
              to{" "}
              <span className="font-medium">
                {(currentPage - 1) * itemsPerPage + transactions.length}
              </span>{" "}
              of <span className="font-medium">{totalItems}</span> results
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="flex gap-1">
                {Array.from({ length: Math.min(3, totalPages) }, (_, i) => {
                  const page = i + 1;
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1 rounded-lg text-sm font-medium ${
                        currentPage === page
                          ? "bg-[#570033] text-white"
                          : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
                {totalPages > 3 && (
                  <>
                    <span className="px-2 py-1 text-gray-500">...</span>
                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      className={`px-3 py-1 rounded-lg text-sm font-medium ${
                        currentPage === totalPages
                          ? "bg-[#570033] text-white"
                          : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      {totalPages}
                    </button>
                  </>
                )}
              </div>
              <button
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                disabled={currentPage === totalPages}
                className="p-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

export default AdminFinances;
